import React, { forwardRef } from "react";
import { useFormik } from "formik";
import {
  generateTimestampAndOffset,
  getCurrentLocalDate,
  getCurrentLocalTime,
} from "../../utils/chartUtils";
import { tempVitalValidation } from "../../utils/validations";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { manualAddTemperatureVital } from "../../HTTPS/VitalApis";
import { usePatient } from "../../context/PatientContext";
import { useToastContext } from "../../context/ToastContext";
import { useUser } from "../../context/UserContext";

function TempVitalForm({ setOpen, temperatureVital }, ref) {
  const { patient } = usePatient();
  const { userDetails } = useUser();
  const queryClient = useQueryClient();
  const { setOpenSuccessToast, setOpenErrorSuccessToast, setToastMessage } =
    useToastContext();

  const { mutate: handleAddVital, isPending } = useMutation({
    mutationFn: ({ temperature, defaultDate, defaultTime }) =>
      patient?.providerUserId
        ? manualAddTemperatureVital(
            patient?.providerUserId,
            userDetails?.id,
            temperature,
            ...generateTimestampAndOffset(defaultDate, defaultTime)
          )
        : Promise.reject(),
    onSuccess: () => {
      setTimeout(() => {
        queryClient.invalidateQueries(["temperatureGraph", patient?.id]);
        queryClient.invalidateQueries(["temperatureVital", patient?.id]);
        queryClient.invalidateQueries(["TempVitalList", patient?.id]);
        
      }, 1000);
      setOpen(false);
      setToastMessage("Vital Added Successfully");
      setOpenSuccessToast(true);
    },
    onError: () => {
      setToastMessage("Failed Adding Vital");
      setOpenErrorSuccessToast(true);
    },
  });

  const formInitialValues = () => ({
      temperature: "",
      defaultDate: getCurrentLocalDate(),
      defaultTime: getCurrentLocalTime(),
    })

  const formik = useFormik({
    initialValues: formInitialValues(),
    validationSchema: tempVitalValidation(),
    onSubmit: (values, { resetForm }) => {
      handleAddVital(values, {
        onSuccess: () => {},
        onError: () => {},
      });
      resetForm({
        values: formInitialValues()
      })
    },
  });

  return (
    <>
      <form className="bg-white z-50 p-5 rounded-2xl">
        <h1 className="text-2xl mb-3">Add Data</h1>
        <div className="flex flex-col space-y-2">
          {/* TEMP */}
          <div className="flex flex-col space-y-1">
            <label htmlFor="dys">Temperature ({temperatureVital?.unit})</label>
            <input
              type="text"
              {...formik.getFieldProps("temperature")} // name, value, onChange, onBlur
              placeholder="Temperature"
              className="border w-72 p-2 rounded-xl"
            />
            {formik.touched.temperature && formik.errors.temperature ? (
              <span className="text-red-500 text-sm">
                {formik.errors.temperature}
              </span>
            ) : null}
          </div>

          {/* DATE */}
          <div className="flex flex-col space-y-1">
            <label htmlFor="">Date</label>
            <input
              type="date"
              {...formik.getFieldProps("defaultDate")} // name, value, onChange, onBlur
              max={getCurrentLocalDate()}
              placeholder="Date"
              className="border w-72 p-2 rounded-xl"
            />
            {formik.touched.defaultDate && formik.errors.defaultDate ? (
              <span className="text-red-500 text-sm">
                {formik.errors.defaultDate}
              </span>
            ) : null}
          </div>

          {/* TIME */}
          <div className="flex flex-col space-y-1">
            <label htmlFor="">Time</label>
            <input
              type="time"
              {...formik.getFieldProps("defaultTime")} // name, value, onChange, onBlur
              placeholder="Time"
              className="border w-72 p-2 rounded-xl"
            />
            {formik.touched.defaultTime && formik.errors.defaultTime ? (
              <span className="text-red-500 text-sm">
                {formik.errors.defaultTime}
              </span>
            ) : null}
          </div>

          <div className="flex">
            <button
              type="submit"
              onClick={formik.handleSubmit}
              disabled={isPending}
              className="bg-[#ED7117] rounded-xl p-3 px-7 text-white"
            >
              Save
            </button>
            <button
              onClick={() => setOpen(false)}
              className="rounded-xl p-3 px-7 text-black"
            >
              Cancel
            </button>
          </div>
        </div>
      </form>
    </>
  );
}

export default forwardRef(TempVitalForm);
